
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { Doughnut } from 'react-chartjs-2';
import * as LucideIcons from 'lucide-react';
import { useAppContext } from '../AppContext';
import { useTranslation } from 'react-i18next';

const CategoryBreakdown = () => {
  const { userId, db, appId } = useAppContext();
  const { t } = useTranslation();
  const [spendingData, setSpendingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId || !db) return;

    const transQuery = query(collection(db, `artifacts/${appId}/users/${userId}/transactions`));

    const unsubscribe = onSnapshot(transQuery, (snapshot) => {
      const categories = {};
      let totalSpending = 0;

      snapshot.docs.forEach(doc => {
        const tx = doc.data();
        if (tx.amount < 0) {
          const category = tx.type || 'uncategorized';
          const amount = Math.abs(tx.amount);
          if (!categories[category]) {
            categories[category] = 0;
          }
          categories[category] += amount;
          totalSpending += amount;
        }
      });

      const sortedCategories = Object.entries(categories)
        .sort(([, a], [, b]) => b - a)
        .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});

      setSpendingData({ categories: sortedCategories, totalSpending });
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [userId, db, appId]);

  const chartData = useMemo(() => {
    if (!spendingData) return { labels: [], datasets: [] };
    const labels = Object.keys(spendingData.categories).map(category => t(`categoryBreakdown.categories.${category.toLowerCase()}`),);
    const data = Object.values(spendingData.categories);
    return {
      labels,
      datasets: [{
        data,
        backgroundColor: ['#8b5cf6', '#ec4899', '#f97316', '#facc15', '#4ade80', '#38bdf8', '#6366f1'],
        borderColor: '#1f2937',
        borderWidth: 2,
      }]
    };
  }, [spendingData, t]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'right', labels: { color: '#d1d5db' } },
      title: { display: false }
    },
    cutout: '60%',
  };

  if (isLoading) {
    return <div className="text-center p-8"><p className="text-white">{t('categoryBreakdown.analyzing')}</p></div>;
  }

  if (!spendingData || spendingData.totalSpending === 0) {
    return (
        <div className="text-center text-gray-400 italic py-8">
            <LucideIcons.PieChart size={48} className="mx-auto mb-4" />
            <p>{t('categoryBreakdown.noData')}</p>
        </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6">
      <div className="h-64 relative"><Doughnut data={chartData} options={chartOptions} /></div>
      <div className="space-y-3 max-h-[40vh] overflow-y-auto custom-scrollbar pr-2">
        {Object.entries(spendingData.categories).map(([category, amount]) => (
          <div key={category} className="bg-gray-800/70 p-3 rounded-lg">
            <div className="flex justify-between items-center">
              <p className="font-semibold text-white capitalize">{t(`categoryBreakdown.categories.${category.toLowerCase()}`)}</p>
              <p className="font-bold text-lg text-red-400">-{amount.toLocaleString()}</p>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2 mt-1">
              <div 
                className="bg-blue-500 h-2 rounded-full"
                style={{ width: `${(amount / spendingData.totalSpending) * 100}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoryBreakdown;
